import * as THREE from "three";
import { Viewer } from "../Viewer-Factory/Viewer";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import cloneDeep from 'lodash/cloneDeep';
import { getCenterPoint, GetSizePosArr } from "./Common";
import { DebugDrawManager } from "./DebugDraw";
import { RefreshModuleLabels } from "./ModuleControl";

/** Sprite tree-id labels for orthographic snapshots (tree-cutting thumbnails / preview). */
export function showTreeLabelsForSnapshot(self: Viewer) {
    self.clearTreeLabel();
    self.treeTagGroup.clear();
    self.treeGroup.children.forEach((tree: any) => {
        const { userData, position, visible, delStatus } = tree;
        if (!visible || !userData?.treeId) return;

        const crownMesh = tree.children?.find((child: any) => child.name === 'crown') ?? tree.children?.[0];
        const crownHeight = crownMesh?.scale?.y ?? 2;
        const labelY = (crownMesh?.position?.y ?? tree.totalH ?? userData.height ?? 4) + crownHeight * 0.35;

        const labelObj = MakeTextSprite(`${userData.treeId}`, {
            color: delStatus ? '#ff5555' : '#ffffff',
            size: delStatus ? 84 : 72,
            x: position.x,
            y: labelY,
            z: position.z,
            visible: true,
        });
        self.treeTagGroup.add(labelObj);
    });
    self.treeTagGroup.visible = true;
}

export function ToggleTreeLabel(self: Viewer, forReport?: boolean, heightLabel?: boolean) {
    if (self.treeTagGroup.visible) {
        self.treeTagGroup.visible = false;
        self.treeTagGroup.clear();
    } else {
        self.treeTagGroup.clear();
        self.treeGroup.children.forEach((tree: any) => {
            const { userData, totalH, position, visible } = tree;
            const options = {
                color: forReport ? '#00ffff' : '#00ffff',
                size: forReport ? 80 : 60,
                x: forReport ? position.x - 2.0 : position.x,
                y: totalH,
                z: forReport ? position.z + 3.0 : position.z,
                visible
            };
            let height = 0, unit = "";
            if (heightLabel) {
                //User is using feet for tree heights
                if (localStorage.getItem('treeUnitOfMeasure') === "\"ft\"") {
                    height = userData.height * 3.28084; //Convert from m to ft
                    unit = "ft";
                }

                //User is using inches for tree heights
                else if (localStorage.getItem('treeUnitOfMeasure') === "\"in\"") {
                    height = userData.height * 39.3701; //Convert from m to in
                    unit = "in";
                }

                //User is using meters for tree heights
                else {
                    height = userData.height;
                    unit = "m";
                }
            }
            const label = heightLabel ? `${Math.round(height)}${unit}` : `${userData.treeId}`;
            let labelObj = forReport ? MakeTextSprite(label, options) : MakeCSSLabel(label, options);

            // let labelObj = MakeTextSprite(label, options);
            self.treeTagGroup.add(labelObj);
        });
        self.treeTagGroup.visible = true;
    }
}

export function ToggleModuleMultiplierLabel(self: Viewer, visible: boolean) {
    if (visible) {
        // Refresh content first so loading spinners replace stale "M 0" values
        RefreshModuleLabels(self);
    }
    for (const module of self.moduleMeshArr) {
        for (const child of module.children as any[]) {
            if (child.meshType === 'moduleLabel') {
                child.visible = visible;
                const el = child.element as HTMLElement | undefined;
                if (el?.style) {
                    el.style.display = visible ? '' : 'none';
                }
            }
        }
    }
}

export function ToggleTreeMultiplierLabel(self: Viewer, show: boolean) {
    self.treeLabelGroup.children.forEach((label) => {
        label.visible = show;
        const el = (label as any).element as HTMLElement | undefined;
        if (el?.style) {
            el.style.display = show ? '' : 'none';
        }
    })
}

export function ReLabelTreeGroup(self: Viewer) {
    const treeArr = [];

    self.treeGroup.children.forEach((tree: any) => {
        const { position, children } = tree, crownMesh: any = children[0], trunkMesh = children[1];
        const stored = tree.userData ?? {};
        const crownScl = crownMesh?.crownScl ?? { x: 1, y: 1, z: 1 };
        const crown_radius = stored.crown_radius ?? crownMesh.scale.x / crownScl.x;
        const crown_height = stored.crown_height ?? crownMesh.scale.y / crownScl.y;
        const trunk_radius = stored.trunk_radius ?? trunkMesh.scale.x;
        const heightValueInMeters = stored.height ?? tree.totalH;

        //generate each tree a alphabets id like A...Z, AA...ZZ, AAA...ZZZ
        const treeId = treeArr.length === 0 ? 'A' : treeArr.length < 26 ? String.fromCharCode(65 + treeArr.length) : String.fromCharCode(65 + Math.floor(treeArr.length / 26) - 1) + String.fromCharCode(65 + treeArr.length % 26);

        tree.treeId = treeId;

        tree.totalH = heightValueInMeters;
        tree.userData = {
            ...stored,
            treeId: treeId,
            crown_radius: crown_radius,
            crown_height,
            trunk_radius,
            height: heightValueInMeters,
            type: stored.type ?? 'ellipsoid',
            x: position.x,
            y: -position.z,
            isAddedToTreeCart: stored.isAddedToTreeCart ?? false
        }

        treeArr.push({
            treeId: treeId,
            crown_radius: crown_radius,
            crown_height,
            trunk_radius,
            height: heightValueInMeters,
            type: 'ellipsoid',
            x: position.x,
            y: -position.z
        });
    });
}

export function ToggleRoofTag(self: Viewer, show: boolean) {
    self.roofTagGroup.clear()
    self.roofTagGroup.visible = show;
    if (!show) return;

    SetRoofTagPos(self);

    self.roofMeshArr.forEach(mesh => {
        //create CSS 2D Label
        const label = document.createElement('div');
        const labelPosition = mesh.roofTagPos;
        label.className = `roofTagLabel ${labelPosition.below ? 'below' : ''}`;
        label.textContent = `Roof ${mesh.userData.nameOverride || mesh.userData.roof_tag_id}\nAzi : ${Math.round(mesh.oriAng.azimuth)}\nTlt : ${Math.round(mesh.userData.pitchOverride)}`;

        const labelTag = new CSS2DObject(label);
        labelTag.position.copy(labelPosition);
        labelTag.name = 'roofTagLabel'
        labelTag.visible = show

        self.roofTagGroup.add(labelTag);
    })


}

export function ToggleRoofEdgeTypeLabel(self: Viewer, show: boolean) {
    DebugDrawManager.execute(self, 'toggleRoofEdgeTypeLabels', { show });
}

export function ToggleRoofIDTriangle(self: Viewer, show: boolean) {

    self.roofIDTriangleGroup.clear()
    if (!show) return;

    const loader = new THREE.TextureLoader();
    const geometry = new THREE.PlaneGeometry(2, 2);

    SetRoofTagPos(self, true);
    self.roofMeshArr.forEach((mesh, idx) => {

        if (self.checkIfRoofIsSmallFace(mesh)) {
            return
        }

        // SVG data
        const svgData =
            `
<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
  <polygon points="50,10 90,80 10,80" style="fill:${mesh.roofTagPos.below ? 'grey' : 'white'};stroke:black;stroke-width:3" />
  <text x="50%" y="50%" text-anchor="middle" fill="black" font-size="28" font-family="Arial" font-weight="bolder" dy=".3em">${mesh.userData.nameOverride ?? mesh.userData.roof_tag_id}</text>
</svg>
`;

        // Convert SVG to texture
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(svgBlob);

        const texture = loader.load(svgUrl, function (texture) {
            texture.needsUpdate = true;
        });

        //make texture sharp and not blurry
        texture.anisotropy = self.renderer.capabilities.getMaxAnisotropy();


        // Create material and geometry
        const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
        if (material.map) material.map.minFilter = THREE.LinearFilter;
        const triangleMesh = new THREE.Mesh(geometry, material);
        triangleMesh.position.copy(mesh.roofTagPos);
        triangleMesh.rotation.x = -Math.PI / 2;
        self.roofIDTriangleGroup.visible = show;
        self.roofIDTriangleGroup.add(triangleMesh);
    });
}

/**
 * High-contrast roof-number labels centered on each roof face (blue fill, white outline).
 * Does not use / clear roof ID triangles — call ToggleRoofIDTriangle separately when needed.
 */
export function ToggleRoofIDNumberText(self: Viewer, show: boolean) {
    self.roofIDNumberGroup.clear();
    if (!show) {
        self.roofIDNumberGroup.visible = false;
        return;
    }

    const loader = new THREE.TextureLoader();
    const geometry = new THREE.PlaneGeometry(2.4, 2.4);

    self.roofMeshArr.forEach((mesh) => {
        if (self.checkIfRoofIsSmallFace(mesh)) return;

        const roofNumber = mesh.userData.nameOverride ?? mesh.userData.roof_tag_id;
        if (roofNumber == null || roofNumber === '') return;

        const center = getCenterPoint(mesh);
        const svgData = `
<svg width="128" height="128" xmlns="http://www.w3.org/2000/svg">
  <text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle"
    fill="#ffffff" stroke="#ffffff" stroke-width="14" stroke-linejoin="round"
    font-size="56" font-family="Arial, Helvetica, sans-serif"
    font-weight="800">${roofNumber}</text>
  <text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle"
    fill="#1d4ed8" font-size="56" font-family="Arial, Helvetica, sans-serif"
    font-weight="800">${roofNumber}</text>
</svg>`;

        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        const texture = loader.load(svgUrl, (loaded) => {
            loaded.needsUpdate = true;
            URL.revokeObjectURL(svgUrl);
        });
        texture.anisotropy = self.renderer.capabilities.getMaxAnisotropy();

        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide,
            toneMapped: false,
        });
        if (material.map) material.map.minFilter = THREE.LinearFilter;

        const labelMesh = new THREE.Mesh(geometry, material);
        labelMesh.position.set(center.x, center.y + 0.35, center.z);
        labelMesh.rotation.x = -Math.PI / 2;
        labelMesh.renderOrder = 10;
        self.roofIDNumberGroup.add(labelMesh);
    });

    self.roofIDNumberGroup.visible = true;
}

export function UpdatePitchOverrideText(self: Viewer, roofTagId: number, newPitchOverride: number) {
    // Find the corresponding label for the given roofTagId
    const roofTagLabel: any = self.roofTagGroup.children.find((tag: any) => {
        return tag.element && tag.element.textContent.includes(`Roof ${roofTagId}`);
    });

    if (roofTagLabel) {
        // Update the text content with the new pitchOverride value
        const labelElement = roofTagLabel.element;

        // Extract other existing values like azimuth to keep them unchanged
        const azimuthMatch = labelElement.textContent.match(/Azi\s*:\s*(\d+)/);
        const azimuth = azimuthMatch ? azimuthMatch[1] : 0; // Fallback to 0 if not found

        const correspondingMesh = self.roofMeshArr.find(mesh => mesh.userData.roof_tag_id === roofTagId);
        // Update the text with the new pitchOverride
        const roofName = correspondingMesh?.userData.nameOverride ?? roofTagId;
        labelElement.textContent = `Roof ${roofName}\nAzi : ${azimuth}\nTlt : ${Math.round(newPitchOverride)}`;
        // Optionally, you can update the roof's `userData.pitchOverride` directly if needed
        if (correspondingMesh) {
            correspondingMesh.userData.pitchOverride = newPitchOverride;
        }

        console.log(`Updated Roof ${roofTagId} with new pitchOverride: ${Math.round(newPitchOverride)}`);
    } else {
        console.error(`Roof tag with ID ${roofTagId} not found.`);
    }
};


export interface TextSpriteProps {
    size: number; // Font size
    x: number; // X position
    y: number; // Y position
    z: number; // Z position
    color?: string; // Text color
    visible?: boolean; // Visibility of the label
}

export function MakeTextSprite(text: string, options: TextSpriteProps) {
    var treeLabelCanvas = document.createElement('canvas');
    var context = treeLabelCanvas.getContext('2d');
    if (context) {
        var textWidth = context.measureText(text).width;
        context.fillStyle = 'rgba(0, 0, 0, 0.3)';
        let w = textWidth * 11;
        context.fillRect(0, 0, w, 100); // Rectangle background
        var x = 10;
        context.font = `bold ${options.size}px Helvetica`;
        context.fillStyle = "white"; // text color
        context.strokeStyle = 'black';  // stroke color
        context.lineWidth = 1; // stroke width
        context.fillText(text, x, options.size);
        context.strokeText(text, x, options.size);
    }
    var texture = new THREE.Texture(treeLabelCanvas) // Canvas content to texture
    texture.minFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    var spriteMaterial = new THREE.SpriteMaterial({ map: texture });
    spriteMaterial.depthTest = false
    spriteMaterial.depthWrite = false
    //increase sprite material brightness
    spriteMaterial.color.setRGB(1.5, 1.5, 1.5);
    var sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(10, 5, 1.0);
    sprite.center.set(0, 0);
    sprite.position.set(options.x, options.y, options.z);
    return sprite;
}

export function MakeCSSLabel(text: string, options: TextSpriteProps) {
    const treeLabelDiv = document.createElement('div');
    treeLabelDiv.className = 'tree-label';
    if (options.color) treeLabelDiv.style.color = options.color;
    treeLabelDiv.textContent = text;
    const treeLabelObj: any = new CSS2DObject(treeLabelDiv);
    if (options.color) treeLabelObj.labelColor = options.color;
    treeLabelObj.position.set(options.x, options.y, options.z);
    treeLabelObj.name = text;
    treeLabelObj.visible = options.visible ?? true;
    return treeLabelObj
}

export function SetRoofTagPos (self: Viewer, forReport?: boolean) {
    const buffer = 0.8, vertOffset = forReport ? 4 : 0.5;
    const midpoints = self.roofMeshArr.map(m => getCenterPoint(m));
    const minMaxArr = self.roofMeshArr.map(m => GetSizePosArr(m.pos2DArr).minMax);
    
    self.roofMeshArr.forEach((mesh, roofIdx) => {
        const point = midpoints[roofIdx];
        const tagPos: any = cloneDeep(midpoints[roofIdx]);
        // const selRoofPos = posSizes[roofIdx];
        midpoints.forEach((p, i) => {
            if (roofIdx === i) return;
            const isOverlap = Math.abs(p.x - point.x) < buffer && Math.abs(p.z - point.z) < buffer;
            if (isOverlap) {
                const centerZ = (point.z + p.z) / 2;
                tagPos.z = (point.z > p.z) ? centerZ + buffer : centerZ - buffer;
                // tagPos.below = point.y < p.y
            }
            const m = minMaxArr[i];
            if ((tagPos.x > m.minX) && (tagPos.x < m.maxX)
                && (tagPos.y < p.y)
                && (tagPos.z > m.minZ) && (tagPos.z < m.maxZ)) {
                tagPos.below = true;
            }
        });

        if (vertOffset) tagPos.y += vertOffset;
        mesh.roofTagPos = tagPos;
    });
 }